import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { getAuth } from 'firebase/auth';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SortButton from './SortButton';
import Pick from './Pick';

const ODIKAI_API_BASE = process.env.REACT_APP_ODIKAI_API_BASE || '/api/odikai';
const MAX_DESCRIPTION_LENGTH = 500;

function sanitizeInput(input) {
  // Remove HTML tags, trim, and limit length
  return input.replace(/<[^>]*>?/gm, '').trim().slice(0, MAX_DESCRIPTION_LENGTH);
}

export default function LanguageBuilder() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [description, setDescription] = useState('');
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingStage, setLoadingStage] = useState(0); // 0: idle, 1: converting, 2: optimizing, 3: building picks
  const [apiResponse, setApiResponse] = useState(null);
  const [showNameDialog, setShowNameDialog] = useState(false);
  const [playbookName, setPlaybookName] = useState('');
  const [sortField, setSortField] = useState('weight');
  const [sortDirection, setSortDirection] = useState('desc');
  const [expandedSections, setExpandedSections] = useState({ recommendedPicks: false });
  const [optimizeLanguageOutput, setOptimizeLanguageOutput] = useState(null);

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: '2-digit'
    });
  };

  const formatPercent = (value) => {
    if (!value && value !== 0) return 'N/A';
    return `${(value * 100).toFixed(2)}%`;
  };

  const formatDollar = (value) => {
    if (!value && value !== 0) return 'N/A';
    return `$${value.toFixed(2)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    const cleanDescription = sanitizeInput(description);
    if (!cleanDescription || cleanDescription.length < 50) {
      setError('Please enter a description of at least 50 characters.');
      return;
    }
    setIsLoading(true);
    setLoadingStage(1);
    try {
      // 1. Get strategies from optimize_language
      const auth = getAuth();
      const currentUser = auth.currentUser;
      if (!currentUser) {
        navigate('/login');
        return;
      }
      const idToken = await currentUser.getIdToken(true);
      const langRes = await fetch(`${ODIKAI_API_BASE}/optimize_language`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({ description: cleanDescription })
      });
      if (!langRes.ok) {
        throw new Error(await langRes.text());
      }
      const langData = await langRes.json();
      setOptimizeLanguageOutput(langData);
      setLoadingStage(2);
      // 2. Optimize
      const optimizeRes = await fetch(`${ODIKAI_API_BASE}/optimize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({ strategies: langData.strategies })
      });
      if (!optimizeRes.ok) {
        throw new Error(await optimizeRes.text());
      }
      const optimizeData = await optimizeRes.json();
      setLoadingStage(3);
      // 3. Get picks
      const picksRes = await fetch(`${ODIKAI_API_BASE}/picks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({ portfolio: optimizeData.portfolio })
      });
      if (!picksRes.ok) {
        throw new Error(await picksRes.text());
      }
      const picksData = await picksRes.json();
      setApiResponse({
        ...optimizeData,
        picks: picksData.picks,
        event_name: picksData.event_name,
        event_date: picksData.event_date,
        portfolio_returns: optimizeData.portfolio_returns,
        previous_year_returns: optimizeData.previous_year_returns,
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
      setLoadingStage(0);
    }
  };

  const handleSaveClick = () => {
    setPlaybookName(`Language Playbook ${new Date().toLocaleDateString()}`);
    setShowNameDialog(true);
  };

  const handleSavePlaybook = async () => {
    if (!apiResponse || !user || !playbookName.trim()) return;
    try {
      const playbookId = `${user.uid}_${Date.now()}`;
      const playbookData = {
        user_id: user.uid,
        created_at: new Date().toISOString(),
        last_updated: new Date().toISOString(),
        name: playbookName.trim(),
        event_name: apiResponse.event_name,
        event_date: apiResponse.event_date,
        playbook_type: 'language',
        description: sanitizeInput(description),
        optimize_language_output: optimizeLanguageOutput,
        portfolio: apiResponse.portfolio,
        picks: apiResponse.picks.map(pick => ({
          pick_name: pick.pick,
          opponent_name: pick.opponent,
          budget_amount: pick.augmented_weight,
          american_odds: pick.odds_us,
          decimal_odds: parseFloat(pick.odds),
          supporting_strategies: pick.strategy?.length || 0,
          type: pick.pick_type.toLowerCase()
        })),
        portfolio_returns: apiResponse.portfolio_returns,
        previous_year_returns: apiResponse.previous_year_returns,
      };
      await setDoc(doc(db, 'user_playbooks', playbookId), playbookData);
      navigate('/playbooks');
    } catch (err) {
      setError('Failed to save playbook: ' + err.message);
    }
  };

  const handleDiscard = () => {
    setApiResponse(null);
    setDescription('');
    setLoadingStage(0);
    setIsLoading(false);
    setError(null);
  };

  const handleSort = (field) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const toggleSection = (section) => {
    setExpandedSections({
      ...expandedSections,
      [section]: !expandedSections[section]
    });
  };

  if (isLoading) {
    let stageText = '';
    if (loadingStage === 1) stageText = 'Converting your strategy into a playbook...';
    else if (loadingStage === 2) stageText = 'Optimizing your playbook...';
    else if (loadingStage === 3) stageText = 'Building picks...';
    else stageText = 'Building...';
    return (
      <div className="simple-builder-section">
        <div className="loading-screen">
          <div className="loading-spinner"></div>
          <h2>{stageText}</h2>
          <p>Please wait while we process your strategy.</p>
        </div>
        <style>{`
          .simple-builder-section {
            min-height: calc(100vh - 64px);
            padding: var(--spacing-lg);
            width: 100%;
            max-width: var(--max-width);
            margin: 0 auto;
            display: flex;
            flex-direction: column;
            gap: 2rem;
            box-sizing: border-box;
            align-items: stretch;
          }
          .loading-screen {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            min-height: 60vh;
            background: transparent;
            border-radius: var(--radius-lg);
            box-shadow: none;
            padding: 3rem 2rem 2.5rem 2rem;
            color: var(--text);
            animation: fadeIn 0.7s var(--smooth);
          }
          .loading-spinner {
            width: 48px;
            height: 48px;
            border: 6px solid var(--surface);
            border-top: 6px solid var(--primary);
            border-radius: 50%;
            animation: spin 1s linear infinite;
            margin-bottom: 2rem;
          }
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (apiResponse) {
    return (
      <div className="simple-builder-section">
        <div className="results-view">
          <div className="picks-display">
            <div className="section-header">
              <h2>Your Custom Playbook</h2>
              <div className="results-summary">
                <div className="summary-item">
                  <h3>Event</h3>
                  <p>{apiResponse.event_name || 'No event selected'}</p>
                  <p className="date">{formatDate(apiResponse.event_date)}</p>
                </div>
                <div className="summary-item">
                  <h3>Average Returns</h3>
                  <p>{formatPercent(apiResponse.portfolio_returns)}</p>
                </div>
                <div className="summary-item">
                  <h3>Previous Year Returns</h3>
                  <p>{formatDollar(apiResponse.previous_year_returns)}</p>
                </div>
              </div>
            </div>
            <div className="accordion-sections">
              <div className="accordion-item">
                <button 
                  className="accordion-header"
                  onClick={() => toggleSection('playbookDescription')}
                >
                  <h3>Playbook Description</h3>
                  {expandedSections.playbookDescription ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </button>
                {expandedSections.playbookDescription && (
                  <div className="accordion-content">
                    <div className="description-section" style={{ marginBottom: '2rem', background: 'var(--surface-light)', borderRadius: '12px', padding: '1.5rem' }}>
                      <p style={{ color: 'var(--text)' }}>{sanitizeInput(description)}</p>
                    </div>
                  </div>
                )}
              </div>
              <div className="accordion-item">
                <button 
                  className="accordion-header"
                  onClick={() => toggleSection('recommendedPicks')}
                >
                  <h3>Recommended Picks</h3>
                  {expandedSections.recommendedPicks ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </button>
                {expandedSections.recommendedPicks && (
                  <div className="accordion-content">
                    <div className="sort-controls">
                      <SortButton 
                        field="weight" 
                        label="Sort by Weight" 
                        currentField={sortField}
                        direction={sortDirection}
                        onClick={handleSort}
                      />
                      <SortButton 
                        field="odds" 
                        label="Sort by Odds" 
                        currentField={sortField}
                        direction={sortDirection}
                        onClick={handleSort}
                      />
                      <SortButton 
                        field="strategies" 
                        label="Sort by Support" 
                        currentField={sortField}
                        direction={sortDirection}
                        onClick={handleSort}
                      />
                    </div>
                    {apiResponse.picks
                      .sort((a, b) => {
                        let aValue, bValue;
                        switch (sortField) {
                          case 'weight':
                            aValue = a.augmented_weight;
                            bValue = b.augmented_weight;
                            break;
                          case 'odds':
                            aValue = parseFloat(a.odds);
                            bValue = parseFloat(b.odds);
                            break;
                          case 'strategies':
                            aValue = Array.isArray(a.strategy) ? a.strategy.length : (a.strategy ? 1 : 0);
                            bValue = Array.isArray(b.strategy) ? b.strategy.length : (b.strategy ? 1 : 0);
                            break;
                          default:
                            return 0;
                        }
                        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
                      })
                      .map((pick, index) => (
                        <Pick
                          key={index}
                          pick_name={pick.pick}
                          opponent_name={pick.opponent}
                          type={pick.pick_type}
                          weight={pick.augmented_weight}
                          american_odds={pick.odds_us}
                          decimal_odds={parseFloat(pick.odds)}
                          supporting_strategies={Array.isArray(pick.strategy) ? pick.strategy.length : (pick.strategy ? 1 : 0)}
                          total_strategies={optimizeLanguageOutput?.strategies?.length || 10}
                        />
                      ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          {error && <div className="error-message">{error}</div>}
          <div className="action-buttons">
            <button className="nav-button back-button" onClick={handleDiscard}>
              Discard
            </button>
            <button className="nav-button next-button" onClick={handleSaveClick}>
              Save Playbook
            </button>
          </div>
        </div>
        {showNameDialog && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h3>Name Your Playbook</h3>
              <input
                type="text"
                value={playbookName}
                onChange={(e) => setPlaybookName(e.target.value)}
                placeholder="Enter playbook name"
                className="playbook-name-input"
              />
              <div className="modal-actions">
                <button 
                  className="nav-button back-button" 
                  onClick={() => setShowNameDialog(false)}
                >
                  Cancel
                </button>
                <button
                  className="nav-button next-button"
                  onClick={() => {
                    setShowNameDialog(false);
                    handleSavePlaybook();
                  }}
                  disabled={!playbookName.trim()}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        )}
        <style>{`
          .simple-builder-section {
            min-height: calc(100vh - 64px);
            padding: var(--spacing-lg);
            width: 100%;
            max-width: var(--max-width);
            margin: 0 auto;
            display: flex;
            flex-direction: column;
            gap: 2rem;
            box-sizing: border-box;
            align-items: stretch;
          }
          .results-view {
            width: 100%;
            display: flex;
            flex-direction: column;
            gap: var(--spacing-lg);
          }
          .picks-display {
            background: var(--surface);
            border-radius: var(--radius-lg);
            padding: var(--spacing-lg);
            width: 100%;
            box-sizing: border-box;
          }
          .section-header {
            margin-bottom: var(--spacing-xl);
          }
          .section-header h2 {
            font-size: clamp(1.5rem, 3vw, 1.75rem);
            font-weight: 600;
            color: var(--text);
            margin-bottom: var(--spacing-lg);
          }
          .results-summary {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: var(--spacing-md);
            margin-top: var(--spacing-lg);
          }
          .summary-item {
            background: var(--surface-light);
            padding: var(--spacing-lg);
            border-radius: var(--radius-md);
            text-align: center;
          }
          .summary-item h3 {
            color: var(--text-secondary);
            font-size: clamp(0.9rem, 1.2vw, 1rem);
            margin-bottom: var(--spacing-sm);
            font-weight: 500;
          }
          .summary-item p {
            color: var(--text);
            font-size: clamp(1.1rem, 1.5vw, 1.5rem);
            font-weight: 600;
            margin: 0;
          }
          .summary-item .date {
            color: var(--text-secondary);
            font-size: clamp(0.8rem, 1vw, 0.9rem);
            font-weight: normal;
            margin-top: var(--spacing-xs);
          }
          .accordion-sections {
            display: flex;
            flex-direction: column;
            gap: var(--spacing-lg);
            margin-top: var(--spacing-xl);
          }
          .accordion-item {
            border-radius: var(--radius-md);
            overflow: hidden;
            background: var(--surface-light);
            border: 1px solid rgba(255, 255, 255, 0.05);
            transition: all 0.2s ease;
          }
          .accordion-item:hover {
            border-color: rgba(255, 255, 255, 0.1);
          }
          .accordion-header {
            width: 100%;
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: var(--spacing-lg);
            background: transparent;
            border: none;
            cursor: pointer;
            transition: all 0.2s ease;
            position: relative;
            overflow: hidden;
          }
          .accordion-header::before {
            content: '';
            position: absolute;
            inset: 0;
            background: rgba(255, 255, 255, 0.03);
            opacity: 0;
            transition: opacity 0.2s ease;
          }
          .accordion-header:hover::before {
            opacity: 1;
          }
          .accordion-header h3 {
            color: var(--text);
            font-size: clamp(1.1rem, 1.5vw, 1.25rem);
            font-weight: 600;
            margin: 0;
            display: flex;
            align-items: center;
            gap: 8px;
          }
          .accordion-header svg {
            transition: transform 0.2s ease;
          }
          .accordion-content {
            background: #1E1E1E;
            padding: var(--spacing-lg);
            border-radius: var(--radius-md);
          }
          .sort-controls {
            display: flex;
            gap: var(--spacing-sm);
            margin: var(--spacing-md) 0;
            flex-wrap: wrap;
          }
          .action-buttons {
            display: flex;
            justify-content: flex-end;
            gap: var(--spacing-md);
            margin-top: var(--spacing-lg);
          }
          @media (max-width: 768px) {
            .simple-builder-section {
              padding: var(--spacing-sm);
            }
            .picks-display {
              padding: var(--spacing-lg);
            }
            .results-summary {
              grid-template-columns: 1fr;
            }
            .action-buttons {
              flex-direction: column;
            }
            .action-buttons button {
              width: 100%;
            }
            .accordion-item {
              border-radius: 8px;
            }
            .accordion-header {
              padding: var(--spacing-md);
            }
            .accordion-content {
              padding: 0 var(--spacing-md) var(--spacing-md);
            }
          }
          .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.7);
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 1000;
          }
          .modal-content {
            background: var(--surface);
            padding: var(--spacing-lg);
            border-radius: var(--radius-lg);
            width: 90%;
            max-width: 400px;
            box-sizing: border-box;
          }
          .modal-content h3 {
            margin: 0 0 var(--spacing-md) 0;
            color: var(--text);
            font-size: 1.25rem;
          }
          .playbook-name-input {
            width: 100%;
            padding: var(--spacing-md);
            margin-bottom: var(--spacing-lg);
            background: var(--surface-light);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: var(--radius-md);
            color: var(--text);
            font-size: 1rem;
            box-sizing: border-box;
          }
          .playbook-name-input:focus {
            outline: none;
            border-color: var(--primary);
            box-shadow: 0 0 0 2px rgba(123, 73, 209, 0.1);
          }
          .modal-actions {
            display: flex;
            justify-content: flex-end;
            gap: var(--spacing-md);
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="simple-builder-section">
      <form className="language-builder-form" onSubmit={handleSubmit}>
        <div className="strategy-card">
          <h2 className="strategy-title">Describe Your UFC Betting Strategy</h2>
          <p className="strategy-subtext">Write a few sentences about how you would pick winners in the UFC. Be as detailed or as simple as you like.</p>
          <textarea
            value={description}
            onChange={e => setDescription(e.target.value.slice(0, MAX_DESCRIPTION_LENGTH))}
            placeholder="E.g. I like to pick fighters who are younger, have a longer reach, and are on a winning streak. I avoid heavy favorites and look for value in underdogs."
            rows={6}
            maxLength={MAX_DESCRIPTION_LENGTH}
            className="description-input"
          />
          <div style={{ textAlign: 'right', color: 'var(--text-secondary)', fontSize: '0.95rem', marginBottom: '1rem' }}>
            {description.length}/{MAX_DESCRIPTION_LENGTH} characters
          </div>
          {error && <div className="error-message">{error}</div>}
          <button className="nav-button next-button" type="submit" disabled={isLoading}>
            Build Playbook
          </button>
        </div>
        <div className="navigation">
          <button className="nav-button back-button" type="button" onClick={() => navigate('/playbooks/build')}>
            Back
          </button>
        </div>
      </form>
      <style>{`
        .simple-builder-section {
          min-height: calc(100vh - 64px);
          padding: var(--spacing-lg);
          width: 100%;
          max-width: var(--max-width);
          margin: 0 auto;
          display: flex;
          flex-direction: column;
          gap: 2rem;
          box-sizing: border-box;
          align-items: stretch;
        }
        .language-builder-form {
          width: 100%;
          display: flex;
          flex-direction: column;
          gap: 2rem;
          align-items: center;
        }
        .strategy-card {
          background: var(--surface);
          border-radius: var(--radius-lg);
          padding: clamp(2rem, 5vw, 2.75rem);
          width: 100%;
          max-width: none;
          margin: 0;
          box-sizing: border-box;
          flex: 0 0 auto;
          display: flex;
          flex-direction: column;
          gap: var(--spacing-lg);
          align-items: stretch;
        }
        .strategy-title {
          font-size: clamp(1.3rem, 2.5vw, 1.5rem);
          font-weight: 600;
          color: var(--text);
          margin-bottom: 0.5rem;
          text-align: left;
        }
        .strategy-subtext {
          font-size: 1.05rem;
          color: var(--text-secondary);
          margin-bottom: 1.5rem;
          text-align: left;
        }
        .description-input {
          font-size: 1.1rem;
          border-radius: 8px;
          border: 1px solid var(--surface-light);
          background: var(--surface-light);
          color: var(--text);
          resize: vertical;
          width: 100%;
          box-sizing: border-box;
          min-height: 120px;
          padding: 1rem;
        }
        .description-input:focus {
          outline: none;
          border-color: var(--primary);
          box-shadow: 0 0 0 2px rgba(123, 73, 209, 0.1);
        }
        .nav-button.next-button {
          background: var(--primary);
          color: white;
          border: none;
          padding: 0.75rem 1.5rem;
          border-radius: var(--radius-md);
          font-size: 1rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s var(--bounce);
          min-width: 120px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .nav-button.next-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .navigation {
          display: flex;
          justify-content: flex-start;
          align-items: center;
          width: 100%;
          margin-top: 1.5rem;
        }
        .nav-button.back-button {
          background: var(--surface-light);
          color: var(--text);
          border: 1px solid rgba(255, 255, 255, 0.1);
          padding: 0.75rem 1.5rem;
          border-radius: var(--radius-md);
          font-size: 1rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s var(--bounce);
          min-width: 120px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        @media (max-width: 1024px) {
          .strategy-card {
            max-width: 98vw;
          }
        }
        @media (max-width: 768px) {
          .simple-builder-section {
            padding: var(--spacing-sm);
          }
          .strategy-card {
            padding: var(--spacing-md);
            max-width: 100%;
          }
          .description-input {
            font-size: 1rem;
            min-height: 90px;
          }
        }
        @media (max-width: 480px) {
          .strategy-card {
            padding: var(--spacing-sm);
            border-radius: var(--radius-md);
          }
          .description-input {
            font-size: 0.98rem;
            min-height: 70px;
          }
        }
      `}</style>
    </div>
  );
} 